/**
 * PayrollUnderstanding — Prototype 3
 * Route: /prototypes/payroll-understanding
 *
 * A GM-facing surface whose central question is:
 *   "How do we show that Sonmi understands the payroll process,
 *    not just that it can calculate numbers?"
 *
 * The answer here is composition. There is no upload form at the top,
 * no calculator grid at the bottom. There is a *state of preparation*
 * expressed as a conversation between three regions:
 *
 *   ARRIVED      — what Sonmi has already received and understood
 *   STILL NEEDED — what remains, with an explanation for each item
 *   NEEDS EYES   — exceptions or discrepancies waiting for the GM
 *
 * The bottom of the page states, plainly, what happens when everything
 * arrives. It is not a submit button. It is a description of the future
 * state, framed so the GM understands why they are doing the work.
 */
import React from "react";
import PrototypeShell from "./PrototypeShell";
import PresenceForm from "./PresenceForm";
import { CheckCircle2, Circle, AlertCircle } from "lucide-react";

const ARRIVED = [
  {
    label: "Zenoti Sales-Service report",
    detail: "1,284 rows · Feb 5 – Feb 18 · uploaded by Damon 2 days ago",
    supports: "Massage Therapist Request Bonus · Experience Guide Enhancement Commission",
  },
  {
    label: "Google Reviews (Yext export)",
    detail: "38 reviews · Feb 1 – Feb 20 · imported automatically",
    supports: "Google Reviews Recognition",
  },
  {
    label: "Time records",
    detail: "Verified by Damon on Feb 19",
    supports: "Baseline for all payout calculations",
  },
];

const STILL_NEEDED = [
  {
    label: "Memberships report",
    why: "Without this, the EG Ritual Program commission can't compute — six team members' commission depends on it.",
    who: "You'll pull this from Zenoti (Reports · Memberships · Feb pay period).",
  },
];

const NEEDS_EYES = [
  {
    label: "Two Enhancement rows created by a name not on the active EG roster.",
    detail: "Rows attributed to 'Alani Yanez' (Massage Therapist, not an EG). Sonmi has excluded them from EG commission. Confirm this is correct — or tell me the roster is out of date.",
  },
];

export default function PayrollUnderstanding() {
  const notes = {
    communicates:
      "Sonmi understands where the payroll stands. Not a form. Not a calculator grid. A living account of what has arrived, what is still needed and why, and what small number of items need a human decision. The GM can read this page and know exactly where they are.",
    placeholder: [
      "All examples are Plantation-specific mock data — dates, row counts, names, and phrasing are illustrative.",
      "The 'Ready for review' end-state at the bottom is described textually; the actual package summary UI is not designed yet.",
      "No upload buttons here — this prototype is the *understanding* view, not the intake surface.",
    ],
    decisions: [
      "Should 'Arrived' items be interactively expandable (show row samples, dates, uploader) or stay as short summaries? Currently: short summaries.",
      "Where does exception review happen — inline here, or on a dedicated surface? Currently: inline, with the invitation 'Confirm' or 'Update roster'.",
      "Should the page show what the completed *package* will contain, in preview, before it exists? Currently: described in prose only.",
      "Is 'Ritual Program' the correct term in every phrase (never 'membership commission')? Currently: yes.",
    ],
    assumptions: [
      "The GM is Damon Mitchell (PLT). Prototype does not gate by role — anyone opening the URL sees the same view.",
      "Presence sits quietly on the left, unchanging as the state changes. It is not a status indicator.",
      "Language is relational ('you'll pull this…') not procedural ('upload the memberships CSV').",
    ],
  };

  return (
    <PrototypeShell title="Prototype 03 · Payroll Understanding (GM)" notes={notes}>
      <section className="max-w-5xl mx-auto px-8 lg:px-12 pt-8 pb-24" data-testid="payroll-understanding">
        {/* Header — situates the moment, not the metric */}
        <header className="flex items-start gap-8 mb-16">
          <div className="shrink-0"><PresenceForm size={140} tone="warm" testId="pu-presence" /></div>
          <div className="flex-1 pt-2">
            <h1 className="font-serif-display text-4xl lg:text-5xl leading-[1.05] text-stone-900 " style={{ fontWeight: 400 }}>
              Payroll is close.
              <br />Here is where we are.
            </h1>
            <p className="text-base text-stone-500 mt-4 max-w-xl leading-relaxed">
              Pay period Feb 5 – Feb 18 · closes tomorrow at 5pm. Below: what I already
              have, what I still need, and one item that needs your eyes.
            </p>
          </div>
        </header>

        {/* Three-region composition */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <Region
            testid="region-arrived"
            eyebrow="Arrived · understood"
            icon={<CheckCircle2 size={14} className="text-emerald-600" />}
          >
            <ul className="space-y-5">
              {ARRIVED.map((it) => (
                <li key={it.label} data-testid={`arrived-${it.label}`}>
                  <div className="text-sm text-stone-900" style={{ fontWeight: 500 }}>{it.label}</div>
                  <div className="text-[12px] text-stone-500 mt-0.5">{it.detail}</div>
                  <div className="text-[11px] text-stone-400 italic mt-1">Supports: {it.supports}</div>
                </li>
              ))}
            </ul>
          </Region>

          <Region
            testid="region-still-needed"
            eyebrow="Still needed"
            icon={<Circle size={14} className="text-stone-400" />}
          >
            <ul className="space-y-5">
              {STILL_NEEDED.map((it) => (
                <li key={it.label} data-testid={`needed-${it.label}`}>
                  <div className="text-sm text-stone-900" style={{ fontWeight: 500 }}>{it.label}</div>
                  <div className="text-[13px] text-stone-600 mt-1 leading-relaxed">{it.why}</div>
                  <div className="text-[12px] text-stone-500 italic mt-2">{it.who}</div>
                </li>
              ))}
            </ul>
          </Region>

          <Region
            testid="region-needs-eyes"
            eyebrow="Needs your eyes"
            icon={<AlertCircle size={14} className="text-amber-600" />}
          >
            <ul className="space-y-5">
              {NEEDS_EYES.map((it, i) => (
                <li key={i} data-testid={`eyes-${i}`}>
                  <div className="text-sm text-stone-900 leading-snug" style={{ fontWeight: 500 }}>{it.label}</div>
                  <div className="text-[13px] text-stone-600 mt-2 leading-relaxed">{it.detail}</div>
                  <div className="mt-3 flex gap-3 text-[12px]">
                    <button className="text-stone-800 hover:text-stone-950 underline underline-offset-4" data-testid={`eyes-${i}-confirm`}>Confirm — exclude</button>
                    <span className="text-stone-300">·</span>
                    <button className="text-stone-500 hover:text-stone-800 underline underline-offset-4" data-testid={`eyes-${i}-update`}>Roster is out of date</button>
                  </div>
                </li>
              ))}
            </ul>
          </Region>
        </div>

        {/* Ready-state description — not a button */}
        <div className="mt-24 pt-8 border-t border-stone-200" data-testid="ready-description">
          <div className="text-[10px] uppercase tracking-[0.25em] text-stone-400">When the last item arrives</div>
          <p className="font-serif-display text-2xl lg:text-3xl text-stone-800 mt-3 leading-snug max-w-3xl" style={{ fontWeight: 400 }}>
            The Plantation payroll package will be ready — every calculation confirmed,
            every exclusion accounted for, every additional pay item captured — and
            passed to Katrina for review.
          </p>
        </div>
      </section>
    </PrototypeShell>
  );
}

function Region({ eyebrow, icon, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500">{eyebrow}</div>
      </div>
      {children}
    </div>
  );
}
